import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  TrendingUp,
  ThumbsUp,
  ExternalLink,
  Plus,
  Check
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface Interview {
  id: string;
  expert_name: string;
  transcript_text?: string;
  selected_questions?: any[];
}

interface QuestionAnswer {
  id: string;
  wheel_question_id?: string;
  question_text: string;
  answer_text: string;
  confidence_score: number;
  expert_name: string;
  expert_title?: string;
  expert_company?: string;
  timestamp_in_transcript?: number;
  added_to_wheel: boolean;
  approved: boolean;
  created_at: string;
}

interface ExtractionStats {
  total_questions: number;
  answered: number;
  not_answered: number;
  high_confidence: number;
  medium_confidence: number;
  low_confidence: number;
  average_confidence: number;
  coverage_percentage: number;
}

interface QuestionAnswerExtractionPanelProps {
  interview: Interview;
  onSuccess?: () => void;
}

export function QuestionAnswerExtractionPanel({
  interview,
  onSuccess
}: QuestionAnswerExtractionPanelProps) {
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>([]);
  const [stats, setStats] = useState<ExtractionStats | null>(null);
  const [alreadyExtracted, setAlreadyExtracted] = useState(false);
  const [addingToWheel, setAddingToWheel] = useState<string | null>(null);

  useEffect(() => {
    loadQuestionAnswers();
  }, [interview.id]);

  const loadQuestionAnswers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`/api/interviews/${interview.id}/question-answers`);

      if (!response.ok) {
        throw new Error('Failed to load question answers');
      }

      const data = await response.json();
      setQuestionAnswers(data.questionAnswers || []);
      setAlreadyExtracted(data.questionAnswers.length > 0);

    } catch (err) {
      console.error('[QuestionAnswers] Error loading:', err);
      setError(err instanceof Error ? err.message : 'Failed to load answers');
    } finally {
      setLoading(false);
    }
  };

  const handleExtractAnswers = async () => {
    try {
      setExtracting(true);
      setError(null);

      console.log('[QuestionAnswers] Extracting answers for interview:', interview.id);

      const response = await authFetch(
        `/api/interviews/${interview.id}/question-answers/extract`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to extract answers');
      }

      const data = await response.json();
      console.log('[QuestionAnswers] Extraction complete:', data);

      setQuestionAnswers(data.questionAnswers || []);
      setStats(data.stats);
      setAlreadyExtracted(data.alreadyExtracted);

      if (onSuccess) {
        onSuccess();
      }

    } catch (err) {
      console.error('[QuestionAnswers] Error extracting:', err);
      setError(err instanceof Error ? err.message : 'Failed to extract answers');
    } finally {
      setExtracting(false);
    }
  };

  const handleAddToWheel = async (answerId: string) => {
    try {
      setAddingToWheel(answerId);
      setError(null);

      const response = await authFetch(
        `/api/interviews/${interview.id}/question-answers/${answerId}/add-to-wheel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add to wheel');
      }

      // Reload to get updated status
      await loadQuestionAnswers();

    } catch (err) {
      console.error('[QuestionAnswers] Error adding to wheel:', err);
      setError(err instanceof Error ? err.message : 'Failed to add to wheel');
    } finally {
      setAddingToWheel(null);
    }
  };

  const getConfidenceBadge = (score: number) => {
    if (score >= 0.80) {
      return <Badge className="bg-green-500">High ({Math.round(score * 100)}%)</Badge>;
    } else if (score >= 0.60) {
      return <Badge className="bg-yellow-500">Medium ({Math.round(score * 100)}%)</Badge>;
    } else if (score >= 0.40) {
      return <Badge className="bg-orange-500">Low ({Math.round(score * 100)}%)</Badge>;
    } else {
      return <Badge variant="outline">Very Low ({Math.round(score * 100)}%)</Badge>;
    }
  };

  const canExtract = interview.transcript_text && interview.selected_questions && interview.selected_questions.length > 0;

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Question & Answer Extraction</h3>
          <p className="text-sm text-muted-foreground">
            Extract answers to question-wheel questions from interview transcription
          </p>
        </div>

        <Button
          onClick={handleExtractAnswers}
          disabled={!canExtract || extracting}
          variant={alreadyExtracted ? "outline" : "default"}
        >
          {extracting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Extracting...
            </>
          ) : alreadyExtracted ? (
            <>
              <MessageSquare className="mr-2 h-4 w-4" />
              Re-Extract Answers
            </>
          ) : (
            <>
              <MessageSquare className="mr-2 h-4 w-4" />
              Extract Answers
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!canExtract && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This interview needs a transcription and selected questions before extracting answers.
            Please complete the interview recording and question selection first.
          </AlertDescription>
        </Alert>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Coverage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.coverage_percentage}%</div>
              <p className="text-xs text-muted-foreground">
                {stats.answered} of {stats.total_questions} questions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">High Confidence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.high_confidence}</div>
              <p className="text-xs text-muted-foreground">80-100% confident</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Medium Confidence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.medium_confidence}</div>
              <p className="text-xs text-muted-foreground">60-79% confident</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg. Confidence</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.round(stats.average_confidence * 100)}%</div>
              <p className="text-xs text-muted-foreground">Overall quality</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Extracted Answers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : questionAnswers.length > 0 ? (
        <div className="space-y-4">
          <h4 className="font-semibold text-sm text-muted-foreground">
            Extracted Answers ({questionAnswers.length})
          </h4>

          {questionAnswers.map((qa) => (
            <Card key={qa.id} className="border-l-4" style={{
              borderLeftColor: qa.confidence_score >= 0.80 ? '#22c55e' :
                               qa.confidence_score >= 0.60 ? '#eab308' :
                               '#f97316'
            }}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-base">{qa.question_text}</CardTitle>
                    <CardDescription className="mt-1 flex items-center gap-2 flex-wrap">
                      {getConfidenceBadge(qa.confidence_score)}
                      {qa.added_to_wheel && (
                        <Badge className="bg-blue-500">
                          <Check className="h-3 w-3 mr-1" />
                          In Wheel
                        </Badge>
                      )}
                      {qa.approved && (
                        <Badge className="bg-green-600">
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          Approved
                        </Badge>
                      )}
                    </CardDescription>
                  </div>

                  {!qa.added_to_wheel && qa.wheel_question_id && (
                    <Button
                      size="sm"
                      onClick={() => handleAddToWheel(qa.id)}
                      disabled={addingToWheel === qa.id}
                    >
                      {addingToWheel === qa.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Add to Wheel
                        </>
                      )}
                    </Button>
                  )}

                  {qa.added_to_wheel && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Navigate to question wheel review page
                        window.location.href = `/question-wheel?question=${qa.wheel_question_id}`;
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm leading-relaxed bg-muted p-3 rounded-md">
                    {qa.answer_text}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>
                      {qa.expert_name}
                      {qa.expert_title && ` • ${qa.expert_title}`}
                      {qa.expert_company && ` • ${qa.expert_company}`}
                    </span>
                    {qa.timestamp_in_transcript && (
                      <span>@ {Math.floor(qa.timestamp_in_transcript / 60)}:{String(qa.timestamp_in_transcript % 60).padStart(2, '0')}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : alreadyExtracted ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No answers were extracted with sufficient confidence. Try adjusting the questions or re-extract after improving the transcription.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
